<template>
  <div ref="contentContainerRef" class="content-container">
    <div class="header">
      <div class="title-section">
        <div class="summary-info">
          <span id="summary-badge" class="summary-badge urgent"> 订单出餐 </span>
        </div>
        <div class="restaurant-selector">
          <select
            v-model="selectedShopId"
            class="restaurant-dropdown"
            disabled
            @change="onShopChange"
          >
            <option v-if="shopList.length === 0" disabled>加载中...</option>
            <option v-for="shop in shopList" :key="shop.shopId" :value="shop.shopId">
              {{ shop.shopName }}
            </option>
          </select>
        </div>
        <!-- 新增：刷新时间显示 + 排除未支付开关 -->
        <div class="refresh-time-info">
          <i class="fas fa-clock mr-2"></i>
          <span>{{ lastRefreshTime || '未刷新' }}</span>
          <div class="exclude-paid-toggle">
            <a-switch v-model:checked="excludeUnPaid" @change="onExcludeUnPaidChange" />
            <span class="exclude-paid-label">排除未支付</span>
          </div>
          <div class="order-filter-toggle">
            <a-switch v-model:checked="onlyToday" />
            <span class="order-filter-label">只看今天</span>
          </div>
        </div>
      </div>

      <div class="actions">
        <button class="quick-find-btn" @click="openQuickFind">
          <i class="fas fa-search"></i> 快速查找
        </button>
        <!-- 新增：统一导航菜单 -->
        <div class="nav-dropdown">
          <button class="nav-menu-btn" @click="toggleNavMenu">
            <i class="fas fa-bars"></i> 菜单
            <i class="fas fa-chevron-down"></i>
          </button>
          <div class="nav-menu" :class="{ show: showNavMenu }">
            <button class="nav-menu-item" @click="goToTableManage">
              <i class="fas fa-table"></i> 桌位管理
            </button>
            <button class="nav-menu-item" @click="goToKitchenOrder">
              <i class="fas fa-utensils"></i> 订单出餐
            </button>
            <button class="nav-menu-item" @click="goToOrderHistory">
              <i class="fas fa-receipt"></i> 订单查询
            </button>
            <button class="nav-menu-item" @click="goToDishAssignment">
              <i class="fas fa-tasks"></i> 菜品分配
            </button>
            <button class="nav-menu-item" @click="goToHistory">
              <i class="fas fa-history"></i> 出餐历史
            </button>
            <button class="nav-menu-item" @click="goToFoodManage">
              <i class="fas fa-utensils"></i> 菜品管理
            </button>
          </div>
        </div>

        <div class="filter-dropdown">
          <button
            class="filter-btn"
            :class="{ active: !isAllFilterTypesSelected }"
            @click="toggleFilterMenu"
          >
            <i class="fas fa-filter"></i> 筛选
            <span class="filter-btn-selection">{{ filterButtonText }}</span>
            <i class="fas fa-chevron-down" :class="{ rotated: showFilterMenu }"></i>
          </button>
          <div class="filter-menu" :class="{ show: showFilterMenu }">
            <div class="filter-menu-section">
              <div class="filter-menu-title">
                <span>订单类型</span>
                <span>已选 {{ selectedFilterTypes.length }} 项</span>
              </div>
              <button
                class="filter-menu-item"
                :class="{ active: isAllFilterTypesSelected }"
                @click="toggleAllFilterTypes"
              >
                <span><i class="fas fa-list"></i>全部订单</span>
                <i
                  class="filter-check fas"
                  :class="isAllFilterTypesSelected ? 'fa-check-square' : 'fa-square'"
                ></i>
              </button>
              <button
                v-for="option in orderTypeFilterOptions"
                :key="option.value"
                class="filter-menu-item"
                :class="{ active: isFilterTypeSelected(option.value) }"
                @click="toggleFilterType(option.value)"
              >
                <span><i class="fas" :class="option.icon"></i>{{ option.label }}</span>
                <i
                  class="filter-check fas"
                  :class="isFilterTypeSelected(option.value) ? 'fa-check-square' : 'fa-square'"
                ></i>
              </button>
            </div>
            <div class="filter-menu-footer">
              <button class="filter-done-btn" @click="showFilterMenu = false">完成</button>
            </div>
          </div>
        </div>

        <button class="refresh-btn" @click="refreshOrders">
          <i class="fas fa-sync-alt"></i> 刷新
        </button>
      </div>
    </div>

    <!-- 订单出餐 -->
    <div class="order-view active">
      <div class="order-cards">
        <template v-if="filteredOrderList.length > 0">
          <div
            v-for="order in filteredOrderList"
            :key="order.orderId"
            class="order-card"
            :class="{ 'quick-find-highlight': highlightedOrderId === order.orderId }"
            :data-order-id="order.orderId"
          >
            <div
              class="order-header"
              :class="{
                'takeaway-header': order.dineType === 2,
                'delivery-header': order.dineType === 3,
                'reservation-header': order.dineType === 5,
              }"
            >
              <div v-if="shouldShowAppointmentTime(order)" class="self-pickup-action-row">
                <button
                  v-if="shouldShowSendToKitchen(order)"
                  class="send-to-kitchen-btn"
                  @click="sendToKitchen(order)"
                >
                  <i
                    class="fas mr-2"
                    :class="isReservationOrder(order) ? 'fa-chair' : 'fa-paper-plane'"
                  ></i
                  >{{ isReservationOrder(order) ? '确定桌号' : '发送到厨房' }}
                </button>
                <div class="self-pickup-time">
                  <i class="fas fa-clock mr-2"></i>{{ getAppointmentTimeLabel(order) }}：{{
                    formatSelfPickupTime(order) || '待确认'
                  }}
                </div>
              </div>

              <div class="order-header-row">
                <div class="restaurant-name">
                  {{ order.shopName
                  }}<span v-if="getDisplayPickCode(order)">
                    - 取餐码：{{ getDisplayPickCode(order) }}</span
                  >
                </div>
                <div
                  class="order-pay-status"
                  :class="{
                    unpaid:
                      order.paymentStatusName &&
                      (order.paymentStatusName.includes('未') ||
                        order.paymentStatusName.includes('待')),
                    paid:
                      order.paymentStatusName &&
                      (order.paymentStatusName.includes('已支付') ||
                        order.paymentStatusName.includes('支付成功') ||
                        order.paymentStatusName.includes('paid') ||
                        order.paymentStatusName.includes('success')),
                  }"
                >
                  {{ order.paymentStatusName || '未知' }}
                </div>
              </div>
              <div class="order-timestamp-row">
                <div class="order-time-info">
                  <span class="order-timestamp">{{ formatDateTime(order.createTime) }}</span>
                  <span
                    v-if="getElapsedMinutes(order.createTime) !== null"
                    class="order-elapsed-time"
                    :class="getElapsedTimeClass(order.createTime)"
                    :aria-label="`已创建${getElapsedMinutes(order.createTime)}分钟`"
                  >
                    <i class="fas fa-clock mr-1" aria-hidden="true"></i
                    >{{ getElapsedMinutes(order.createTime) }}
                  </span>
                </div>
                <span class="order-amount">¥{{ (order.totalAmount || 0).toFixed(2) }}</span>
              </div>
              <div class="order-header-details">
                <div class="order-info-left">
                  <div
                    class="order-id"
                    role="button"
                    tabindex="0"
                    aria-label="查看订单详情"
                    @click.stop="openOrderDetails(order)"
                    @keydown.enter.prevent="openOrderDetails(order)"
                    @keydown.space.prevent="openOrderDetails(order)"
                  >
                    <span class="order-id-prefix">
                      订单号： {{ getOrderNoPrefix(order.orderNo) }}
                    </span>
                    <span class="order-id-suffix">{{ getOrderNoSuffix(order.orderNo) }}</span>
                    <!-- 暂时移除加急标识，因为数据模型中没有isUrgent字段 -->
                    <!-- <span v-if="order.isUrgent" class="urgent-badge">加急</span> -->
                  </div>
                  <div class="order-table-row">
                    <div class="order-table notranslate" translate="no">
                      {{ order.tableName }}
                      <span
                        v-if="getDineTypeText(order)"
                        class="order-type-badge"
                        :class="getDineTypeClass(order)"
                      >
                        {{ getDineTypeText(order) }}
                      </span>
                    </div>
                    <button
                      v-if="Number(order.orderType) === 1"
                      class="seat-number-btn"
                      :title="order.seatNumber ? '修改座位号' : '添加座位号'"
                      @click="openSeatNumberModal(order)"
                    >
                      <i class="fas fa-chair mr-1"></i>{{ order.seatNumber || '添加座位号' }}
                    </button>
                  </div>
                  <div v-if="isReservationOrder(order)" class="order-reservation-count">
                    <i class="fas fa-users mr-1"></i>预定人数：{{ order.personCount ?? 0 }}人
                  </div>
                  <div v-if="isDeliveryOrder(order)" class="delivery-address">
                    <i class="fas fa-map-marker-alt mr-1"></i
                    >{{ order.consigneeAddress || '待填写地址' }}
                  </div>
                  <!-- 新增：备注显示 -->
                  <div v-if="order.description" class="order-remark">
                    <i class="fas fa-sticky-note mr-1"></i>{{ order.description }}
                  </div>
                </div>
                <!-- 新增：确认支付完成按钮 -->
                <div class="order-payment-actions">
                  <button
                    v-if="canShowConfirmPayment(order)"
                    class="confirm-payment-btn"
                    title="确认支付完成"
                    @click="showPaymentConfirmModal(order)"
                  >
                    <i class="fas fa-credit-card mr-2"></i>确认支付
                  </button>
                </div>
              </div>
            </div>

            <div class="order-items">
              <div
                v-for="item in order.foodList || []"
                :key="item.orderFoodId"
                class="order-item"
                :class="{ 'order-item-cancelled': isFoodCancelled(item) }"
              >
                <div class="order-item-content">
                  <div class="item-info">
                    <div class="item-name">{{ foodDisplayName(item) }}</div>
                    <!-- 新增：口味信息显示 -->
                    <div
                      v-if="item.foodCookDesc && item.foodCookDesc.trim() !== ''"
                      class="item-cook"
                    >
                      <i class="fas fa-utensils mr-1"></i>{{ item.foodCookDesc }}
                    </div>
                  </div>
                  <div class="item-quantity">×{{ item.quantity }}</div>
                </div>
                <button
                  class="item-btn"
                  :disabled="item.status === 9 || isFoodCancelled(item)"
                  :style="{ backgroundColor: item.status === 9 ? '#34C759' : '' }"
                  @click="handleFoodOut(order, item)"
                >
                  {{ isFoodCancelled(item) ? '已取消' : item.status === 9 ? '已出餐' : '出餐' }}
                </button>
              </div>
            </div>

            <div class="order-footer">
              <div class="order-status">{{ getOrderStatus(order) }}</div>
              <button
                v-if="getActiveFoods(order).length > 0 && !isOrderAllCompleted(order)"
                class="complete-all-btn"
                @click="handleCompleteAll(order)"
              >
                全部出餐
              </button>
              <button
                v-else-if="isOrderAllCompleted(order)"
                class="complete-all-btn verify-btn"
                @click="handleVerify(order)"
              >
                核销
              </button>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="no-order-tip">暂无订单</div>
        </template>
      </div>
    </div>

    <van-dialog
      v-model:show="quickFindVisible"
      title="按订单尾号查找"
      :show-confirm-button="false"
      @closed="onQuickFindClosed"
    >
      <div class="quick-find-dialog">
        <span>请输入订单号后四位</span>
        <div class="quick-find-digits" :aria-label="`已输入${quickFindInput.length}位`">
          <span v-for="index in 4" :key="index" class="quick-find-digit">
            {{ quickFindInput[index - 1] || '—' }}
          </span>
        </div>
        <div class="quick-find-keypad">
          <button
            v-for="digit in 9"
            :key="digit"
            type="button"
            @click="appendQuickFindDigit(String(digit))"
          >
            {{ digit }}
          </button>
          <button type="button" class="quick-find-key-action" @click="quickFindInput = ''">
            清空
          </button>
          <button type="button" @click="appendQuickFindDigit('0')">0</button>
          <button
            type="button"
            class="quick-find-key-action"
            aria-label="删除上一位"
            @click="quickFindInput = quickFindInput.slice(0, -1)"
          >
            <i class="fas fa-backspace" aria-hidden="true"></i>
          </button>
        </div>
        <div class="quick-find-dialog-actions">
          <button type="button" @click="quickFindVisible = false">取消</button>
        </div>
      </div>
    </van-dialog>

    <!-- POS 订单座位号选择模态框 -->
    <div
      v-if="seatNumberModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="seat-number-modal bg-white rounded-xl p-5 w-full flex flex-col overflow-hidden">
        <div class="flex justify-between items-center mb-4 flex-shrink-0">
          <div>
            <h3 class="text-xl font-bold">选择座位号</h3>
            <p class="text-sm text-gray-500 mt-1">
              {{ currentSeatOrder?.tableName || '' }} · {{ currentSeatOrder?.orderNo || '' }}
            </p>
          </div>
          <button
            class="text-gray-500 hover:text-gray-700"
            :disabled="isEditingSeatNumber"
            @click="closeSeatNumberModal"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div v-if="isLoadingSeatTables" class="py-10 text-center text-gray-500">
          <i class="fas fa-spinner fa-spin mr-2"></i>正在加载桌位...
        </div>
        <div v-else-if="seatTableList.length === 0" class="py-10 text-center text-gray-500">
          暂无可选桌位
        </div>
        <div v-else class="seat-table-grid">
          <button
            v-for="table in seatTableList"
            :key="table.tableId"
            class="seat-table-option"
            :class="{ selected: selectedSeatNumber === table.tableName }"
            :disabled="isEditingSeatNumber || !table.tableName"
            @click="selectSeatNumber(table)"
          >
            <span>{{ table.tableName || '未命名桌位' }}</span>
            <i
              v-if="isEditingSeatNumber && selectedSeatNumber === table.tableName"
              class="fas fa-spinner fa-spin"
            ></i>
            <i v-else-if="selectedSeatNumber === table.tableName" class="fas fa-check"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- 预定订单选桌模态框 -->
    <div
      v-if="reservationTableModalVisible"
      class="reservation-table-modal-mask fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div
        class="reservation-table-modal bg-white rounded-xl p-6 w-full max-w-lg flex flex-col overflow-hidden"
      >
        <div class="flex justify-between items-center mb-4 flex-shrink-0">
          <div>
            <h3 class="text-xl font-bold">选择桌号</h3>
          </div>
          <button
            class="text-gray-500 hover:text-gray-700"
            :disabled="isChangingReservationTable"
            @click="closeReservationTableModal"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div v-if="isLoadingReservationTables" class="py-10 text-center text-gray-500">
          <i class="fas fa-spinner fa-spin mr-2"></i>正在加载桌位...
        </div>
        <div
          v-else-if="availableReservationTables.length === 0"
          class="py-10 text-center text-gray-500"
        >
          暂无可用普通桌位
        </div>
        <div
          v-else
          class="reservation-table-list grid grid-cols-2 gap-3 overflow-y-auto pr-1 flex-1 min-h-0"
        >
          <button
            v-for="table in availableReservationTables"
            :key="table.tableId"
            class="p-4 border-2 rounded-lg text-left transition-colors"
            :class="
              selectedReservationTableId === table.tableId
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-200 hover:border-purple-300'
            "
            :disabled="isChangingReservationTable"
            @click="selectedReservationTableId = table.tableId || null"
          >
            <div class="font-bold text-lg">{{ table.tableName || '未命名桌位' }}</div>
            <div class="text-sm text-gray-500 mt-1">
              {{ table.seatsDesc || `${table.seats || 0}人桌` }}
            </div>
          </button>
        </div>

        <div class="flex space-x-3 pt-5 mt-5 border-t flex-shrink-0">
          <button
            class="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg"
            :disabled="isChangingReservationTable"
            @click="closeReservationTableModal"
          >
            取消
          </button>
          <button
            class="flex-1 py-3 rounded-lg text-white font-medium"
            :class="
              selectedReservationTableId && !isChangingReservationTable
                ? 'bg-purple-600 hover:bg-purple-700'
                : 'bg-gray-400 cursor-not-allowed'
            "
            :disabled="!selectedReservationTableId || isChangingReservationTable"
            @click="confirmReservationTable"
          >
            <span v-if="isChangingReservationTable">
              <i class="fas fa-spinner fa-spin mr-2"></i>处理中...
            </span>
            <span v-else>确认并发送到厨房</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 新增：支付确认模态框 -->
    <div
      v-if="paymentConfirmModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-xl p-6 w-96 max-w-md max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">选择支付方式</h3>
          <button
            class="text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isConfirmingPayment"
            @click="hidePaymentConfirmModal"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- 订单信息 -->
        <div class="mb-4 p-3 bg-gray-50 rounded-lg">
          <div class="flex justify-between items-center mb-2">
            <span class="text-gray-600">订单号:</span>
            <span class="font-medium">{{ currentPaymentOrder?.orderNo || '' }}</span>
          </div>
          <div class="flex justify-between items-center mb-2">
            <span class="text-gray-600">桌号:</span>
            <span class="font-medium">{{ currentPaymentOrder?.tableName || '' }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-gray-600">应付金额:</span>
            <span class="text-xl font-bold text-red-500">
              ¥{{
                (
                  currentPaymentOrder?.remainAmount ||
                  currentPaymentOrder?.totalAmount ||
                  0
                ).toFixed(2)
              }}
            </span>
          </div>
        </div>

        <div class="space-y-4">
          <!-- 支付方式选择 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"> 支付方式 </label>
            <select
              v-model="selectedPaymentMethod"
              :disabled="isConfirmingPayment"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-lg"
            >
              <option v-for="method in paymentMethods" :key="method.value" :value="method.value">
                {{ method.name }}
              </option>
            </select>
          </div>

          <!-- 备注输入 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"> 备注信息 </label>
            <textarea
              v-model="paymentRemark"
              :disabled="isConfirmingPayment"
              placeholder="请输入备注信息（可选）"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-base resize-none"
            ></textarea>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="flex space-x-3 pt-6 mt-6 border-t">
          <button
            class="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isConfirmingPayment"
            @click="hidePaymentConfirmModal"
          >
            取消
          </button>
          <button
            class="flex-1 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            :disabled="isConfirmingPayment"
            @click="handlePaymentConfirm"
          >
            <span v-if="isConfirmingPayment">
              <i class="fas fa-spinner fa-spin mr-2"></i>处理中...
            </span>
            <span v-else>确认支付</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 扫码支付全屏界面 -->
    <div v-if="scanPaymentModalVisible" class="fixed inset-0 bg-black z-50 flex flex-col">
      <!-- 顶部导航栏 -->
      <div class="bg-black bg-opacity-80 text-white px-4 py-3 flex items-center justify-between">
        <button class="text-white" @click="hideScanPaymentModal">
          <i class="fas fa-times text-2xl"></i>
        </button>
        <h3 class="text-xl font-bold">B扫C收款</h3>
        <div class="w-8"></div>
      </div>

      <!-- 摄像头扫描区域 -->
      <div class="flex-1 relative overflow-hidden w-full h-full">
        <div id="qr-reader" class="qr-reader-container w-full h-full"></div>

        <!-- 动态扫描线 -->
        <div v-if="isScanning" class="absolute inset-0 pointer-events-none z-20">
          <div class="scan-line"></div>
        </div>
      </div>

      <!-- 底部状态提示 -->
      <div v-if="scanStatus" class="bg-black bg-opacity-80 px-4 py-3">
        <div class="p-3 rounded-lg text-center" :class="scanStatusClass">
          <p class="text-sm font-medium">{{ scanStatus }}</p>
        </div>
      </div>
    </div>

    <!-- 扫码支付失败确认模态框 -->
    <div
      v-if="scanPaymentFailModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-xl p-6 w-96 max-w-md">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">支付确认</h3>
          <button class="text-gray-500 hover:text-gray-700" @click="cancelScanPaymentConfirm">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="mb-6">
          <p class="text-gray-700 text-lg">支付结果未确认，请选择下一步操作。</p>
          <p v-if="scanPaymentFailErrorMsg" class="text-red-500 text-sm mt-2">
            错误信息：{{ scanPaymentFailErrorMsg }}
          </p>
        </div>

        <!-- 操作按钮 -->
        <div class="flex flex-col space-y-3">
          <button
            :disabled="isRefreshingPayment"
            :class="[
              'w-full py-3 rounded-lg transition-colors font-medium',
              isRefreshingPayment
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600',
            ]"
            @click="confirmScanPaymentPaid"
          >
            <span v-if="isRefreshingPayment">
              <i class="fas fa-spinner fa-spin mr-2"></i>查询支付结果中
            </span>
            <span v-else>重新查询支付结果</span>
          </button>
          <button
            :disabled="isRefreshingPayment"
            class="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
            @click="retryScanPayment"
          >
            重新扫码支付
          </button>
          <button
            :disabled="isRefreshingPayment"
            class="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
            @click="cancelScanPaymentConfirm"
          >
            取消
          </button>
        </div>
      </div>
    </div>
    <van-popup
      v-model:show="showOrderDetails"
      position="center"
      round
      :style="{ width: '96vw', maxWidth: '1200px', height: '92dvh' }"
      @closed="onOrderDetailsClosed"
    >
      <div class="flex h-full flex-col overflow-hidden bg-white">
        <div class="flex shrink-0 items-center justify-between border-b px-4 py-3">
          <span class="font-semibold">订单详情</span>
          <button
            type="button"
            class="px-2 py-1 text-gray-600"
            aria-label="关闭订单详情"
            @click="showOrderDetails = false"
          >
            <i class="fas fa-times mr-1"></i>关闭
          </button>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto">
          <OrderDetailView
            v-if="detailOrder"
            :key="detailOrder.orderId"
            :detail-order="detailOrder"
          />
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
  import { useRouter } from 'vue-router';
  import { fetchShopList, fetchShopDetail, fetchTableList } from '@/service/base';
  import {
    getOrderList,
    getOrder,
    insertOrderPayment,
    auditOrder,
    completeOrder,
    editOrderTable,
    editSeatNumber,
    getOrderTableList,
  } from '@/service/order';
  import { outFood, foodUp } from '@/service/food';
  import { FoodList, OrderListItemModel, OrderTableListItemModel } from '@/model/order';
  import dayjs from 'dayjs';
  import { dateTimeFormat } from '@/utils/utils';
  import { scanPayment, refreshScanPayment } from '@/service/payment';
  import { Html5Qrcode } from 'html5-qrcode';
  import { useCurrentEmployee } from '@/composables/useCurrentEmployee';
  import OrderDetailView from '@/views/order/OrderDetailView.vue';

  const router = useRouter();
  const { employeeId, employeeName } = useCurrentEmployee();
  const currentTimeMs = ref(Date.now());
  const showOrderDetails = ref(false);
  const detailOrder = ref<OrderListItemModel>();
  const openOrderDetails = (order: OrderListItemModel): void => {
    if (!order.orderId) return;
    detailOrder.value = order;
    showOrderDetails.value = true;
  };
  const onOrderDetailsClosed = (): void => {
    detailOrder.value = undefined;
    if (selectedShopId.value) void fetchOrderList(Number(selectedShopId.value));
  };

  // 新增：新订单提示音
  let newOrderAudio: HTMLAudioElement | null = null;
  const initNewOrderAudio = () => {
    try {
      const src = new URL('@/assets/audio/new_order.mp3', import.meta.url).href;
      newOrderAudio = new Audio(src);
      newOrderAudio.preload = 'auto';
      newOrderAudio.volume = 1.0;
    } catch (e) {
      console.warn('新订单提示音初始化失败:', e);
    }
  };
  const playNewOrderSound = async () => {
    try {
      if (newOrderAudio) {
        newOrderAudio.currentTime = 0;
        await newOrderAudio.play();
      }
    } catch (e) {
      console.warn('播放新订单提示音失败:', e);
    }
  };

  // 响应式数据
  const shopList = ref<any[]>([]);
  const selectedShopId = ref<number | null>(null);
  const orderList = ref<Array<OrderListItemModel>>([]);
  const showFilterMenu = ref(false);
  const showNavMenu = ref(false);
  const excludeUnPaid = ref(false);
  const onlyToday = ref(false);
  const contentContainerRef = ref<HTMLDivElement | null>(null);
  const quickFindVisible = ref(false);
  const quickFindInput = ref('');
  const highlightedOrderId = ref<number | null>(null);
  const pendingQuickFindOrderId = ref<number | null>(null);
  let quickFindHighlightTimer: ReturnType<typeof setTimeout> | null = null;

  type OrderTypeFilter = 'dine-in' | 'self-pickup' | 'delivery' | 'packed' | 'reservation';

  interface OrderTypeFilterOption {
    value: OrderTypeFilter;
    label: string;
    icon: string;
    dineType: number;
  }

  const orderTypeFilterOptions: OrderTypeFilterOption[] = [
    { value: 'dine-in', label: '堂食', icon: 'fa-utensils', dineType: 1 },
    { value: 'self-pickup', label: '自提', icon: 'fa-shopping-bag', dineType: 2 },
    { value: 'delivery', label: '外卖', icon: 'fa-motorcycle', dineType: 3 },
    { value: 'packed', label: '打包', icon: 'fa-box', dineType: 4 },
    { value: 'reservation', label: '预定', icon: 'fa-calendar-check', dineType: 5 },
  ];
  const selectedFilterTypes = ref<OrderTypeFilter[]>(
    orderTypeFilterOptions.map((option) => option.value),
  );

  const reservationTableModalVisible = ref(false);
  const currentReservationOrder = ref<OrderListItemModel | null>(null);
  const availableReservationTables = ref<OrderTableListItemModel[]>([]);
  const selectedReservationTableId = ref<number | null>(null);
  const isLoadingReservationTables = ref(false);
  const isChangingReservationTable = ref(false);

  const seatNumberModalVisible = ref(false);
  const currentSeatOrder = ref<OrderListItemModel | null>(null);
  const seatTableList = ref<OrderTableListItemModel[]>([]);
  const selectedSeatNumber = ref('');
  const isLoadingSeatTables = ref(false);
  const isEditingSeatNumber = ref(false);
  const excludedSeatTableNames = new Set(['POS下单', '预定', '外卖', '自提']);

  const closeSeatNumberModal = () => {
    if (isEditingSeatNumber.value) return;
    seatNumberModalVisible.value = false;
    currentSeatOrder.value = null;
    seatTableList.value = [];
    selectedSeatNumber.value = '';
  };

  const openSeatNumberModal = async (order: OrderListItemModel) => {
    if (!order.orderId || !order.shopId) {
      showToast('订单信息不完整，无法修改座位号');
      return;
    }

    currentSeatOrder.value = order;
    selectedSeatNumber.value = order.seatNumber || '';
    seatTableList.value = [];
    seatNumberModalVisible.value = true;
    isLoadingSeatTables.value = true;

    try {
      const response = await getOrderTableList({
        pageNum: 1,
        pageSize: 999,
        data: { shopId: order.shopId },
      });
      if (response.code === 200) {
        seatTableList.value = (response.result.list || []).filter(
          (table) => !excludedSeatTableNames.has(table.tableName?.trim() || ''),
        );
      } else {
        showToast(response.msg || '加载桌位失败');
      }
    } catch (error) {
      console.error('加载座位号桌位列表失败:', error);
      showToast('加载桌位失败，请稍后重试');
    } finally {
      isLoadingSeatTables.value = false;
    }
  };

  const selectSeatNumber = async (table: OrderTableListItemModel) => {
    if (isEditingSeatNumber.value || !table.tableName) return;

    const order = currentSeatOrder.value;
    if (!order?.orderId) {
      showToast('订单信息不完整，无法修改座位号');
      return;
    }

    selectedSeatNumber.value = table.tableName;
    isEditingSeatNumber.value = true;
    try {
      const response = await editSeatNumber({
        orderId: order.orderId,
        seatNumber: table.tableName,
      });
      if (response.code === 200) {
        order.seatNumber = table.tableName;
        showToast(`座位号已修改为 ${table.tableName}`);
        isEditingSeatNumber.value = false;
        closeSeatNumberModal();
      } else {
        showToast(response.msg || '修改座位号失败');
      }
    } catch (error) {
      console.error('修改订单座位号失败:', error);
      showToast('修改座位号失败，请稍后重试');
    } finally {
      isEditingSeatNumber.value = false;
    }
  };

  // 新增：自动刷新相关状态
  const lastRefreshTime = ref('');
  let autoRefreshTimer: NodeJS.Timeout | null = null;

  // 新增：支付确认相关状态
  const paymentConfirmModalVisible = ref(false);
  const isConfirmingPayment = ref(false);
  const selectedPaymentMethod = ref(4); // 默认选择POS支付，如果有B扫C收款会自动更新
  const paymentRemark = ref('');
  const isOnlinePayment = ref(false); // 是否为线上支付
  const currentPaymentOrder = ref<any | null>(null); // 当前确认支付的订单

  // 支付方式列表（计算属性）
  const paymentMethods = computed(() => {
    const methods = [
      { value: 4, name: 'POS支付' },
      { value: 5, name: '公司挂账' },
      { value: 7, name: '现金支付' },
    ];
    // 如果支持在线支付，添加B扫C收款选项
    if (isOnlinePayment.value) {
      methods.unshift({ value: 3, name: 'B扫C收款' });
    }
    return methods;
  });

  // 扫码支付相关状态
  const scanPaymentModalVisible = ref(false);
  const isScanning = ref(false);
  const scanPaymentRemark = ref('');
  const scanStatus = ref('');
  const scanStatusClass = ref('');
  const scanPaymentFailModalVisible = ref(false); // 扫码支付失败确认模态框
  const scanPaymentFailErrorMsg = ref(''); // 扫码支付失败错误信息
  const isRefreshingPayment = ref(false); // 是否正在刷新支付状态
  const isSubmittingScanPayment = ref(false);
  let html5QrCode: Html5Qrcode | null = null;

  // 新增：本地存储最近订单创建时间（按店铺）
  const LAST_ORDER_CREATE_TIME_PREFIX = 'kitchen_order_last_create_time_';
  const getLastCreateTimeKey = (shopId: number) => `${LAST_ORDER_CREATE_TIME_PREFIX}${shopId}`;
  const getStoredLastCreateTime = (shopId: number): number | null => {
    try {
      const v = localStorage.getItem(getLastCreateTimeKey(shopId));
      return v ? parseInt(v, 10) : null;
    } catch {
      return null;
    }
  };
  const setStoredLastCreateTime = (shopId: number, ms: number) => {
    try {
      localStorage.setItem(getLastCreateTimeKey(shopId), String(ms));
    } catch (e) {
      console.warn('无法保存最近创建时间到localStorage:', e);
    }
  };

  // localStorage key for selected shop（与桌位管理共用，确保页面间店铺选择一致）
  const SELECTED_SHOP_KEY = 'table_manage_selected_shop';

  // 保存选中的餐厅到localStorage
  const saveSelectedShop = (shopId: number) => {
    try {
      localStorage.setItem(SELECTED_SHOP_KEY, shopId.toString());
    } catch (error) {
      console.warn('无法保存餐厅选择到localStorage:', error);
    }
  };

  // 从localStorage读取选中的餐厅
  const loadSelectedShop = (): number | null => {
    try {
      const savedShopId = localStorage.getItem(SELECTED_SHOP_KEY);
      return savedShopId ? parseInt(savedShopId, 10) : null;
    } catch (error) {
      console.warn('无法从localStorage读取餐厅选择:', error);
      return null;
    }
  };

  // 验证餐厅是否存在于当前餐厅列表中
  const isValidShop = (shopId: number): boolean => {
    return shopList.value.some((shop) => shop.shopId === shopId);
  };

  const getCreateTimeMs = (order: OrderListItemModel): number => {
    const createTimeMs = order.createTime ? new Date(order.createTime).getTime() : NaN;
    return Number.isNaN(createTimeMs) ? Number.MAX_SAFE_INTEGER : createTimeMs;
  };

  const getAppointmentTimeMs = (order: OrderListItemModel): number | null => {
    if (!order.selfPickDate || !order.selfPickTime) return null;

    const dateMatch = order.selfPickDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const startTime = order.selfPickTime.split('-')[0]?.trim();
    const timeMatch = startTime?.match(/^(\d{2}):(\d{2})$/);
    if (!dateMatch || !timeMatch) return null;

    const [, year, month, day] = dateMatch;
    const [, hour, minute] = timeMatch;
    const appointmentTime = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
    );

    const isValidAppointmentTime =
      appointmentTime.getFullYear() === Number(year) &&
      appointmentTime.getMonth() === Number(month) - 1 &&
      appointmentTime.getDate() === Number(day) &&
      appointmentTime.getHours() === Number(hour) &&
      appointmentTime.getMinutes() === Number(minute);

    return isValidAppointmentTime ? appointmentTime.getTime() : null;
  };

  const getEffectiveOrderTimeMs = (order: OrderListItemModel): number => {
    const usesAppointmentTime = [2, 3, 5].includes(order.dineType ?? 0);
    if (usesAppointmentTime) {
      return getAppointmentTimeMs(order) ?? getCreateTimeMs(order);
    }
    return getCreateTimeMs(order);
  };

  const getLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isTodayOrder = (order: OrderListItemModel): boolean => {
    const today = getLocalDateString(new Date());
    if ([2, 3, 5].includes(order.dineType ?? 0)) {
      return order.selfPickDate?.trim() === today;
    }

    if (!order.createTime) return false;
    const createTime = new Date(order.createTime);
    return !Number.isNaN(createTime.getTime()) && getLocalDateString(createTime) === today;
  };

  // 计算属性
  const isAllFilterTypesSelected = computed(
    () => selectedFilterTypes.value.length === orderTypeFilterOptions.length,
  );

  const filterButtonText = computed(() => {
    if (isAllFilterTypesSelected.value) return '全部订单';
    if (selectedFilterTypes.value.length === 0) return '未选择';

    return orderTypeFilterOptions
      .filter((option) => selectedFilterTypes.value.includes(option.value))
      .map((option) => option.label)
      .join('、');
  });

  const filteredOrderList = computed(() => {
    let result = [...orderList.value];

    if (!isAllFilterTypesSelected.value) {
      const selectedDineTypes = new Set(
        orderTypeFilterOptions
          .filter((option) => selectedFilterTypes.value.includes(option.value))
          .map((option) => option.dineType),
      );
      result = result.filter((order) => selectedDineTypes.has(order.dineType ?? 0));
    }

    if (onlyToday.value) {
      result = result.filter(isTodayOrder);
    }

    return result.sort((a, b) => {
      const effectiveTimeDifference = getEffectiveOrderTimeMs(a) - getEffectiveOrderTimeMs(b);
      if (effectiveTimeDifference !== 0) return effectiveTimeDifference;

      const createTimeDifference = getCreateTimeMs(a) - getCreateTimeMs(b);
      if (createTimeDifference !== 0) return createTimeDifference;

      return (a.orderId ?? Number.MAX_SAFE_INTEGER) - (b.orderId ?? Number.MAX_SAFE_INTEGER);
    });
  });

  const clearQuickFind = () => {
    if (quickFindHighlightTimer) {
      clearTimeout(quickFindHighlightTimer);
      quickFindHighlightTimer = null;
    }
    highlightedOrderId.value = null;
  };

  const openQuickFind = () => {
    quickFindInput.value = '';
    quickFindVisible.value = true;
  };

  const appendQuickFindDigit = (digit: string) => {
    if (!/^\d$/.test(digit) || !quickFindVisible.value) return;
    quickFindInput.value = quickFindInput.value.length === 4 ? digit : quickFindInput.value + digit;
    if (quickFindInput.value.length === 4) findQuickOrder();
  };

  const focusQuickOrder = async (orderId: number) => {
    highlightedOrderId.value = orderId;
    if (quickFindHighlightTimer) clearTimeout(quickFindHighlightTimer);
    await nextTick();
    const card = contentContainerRef.value?.querySelector<HTMLElement>(
      `.order-card[data-order-id="${orderId}"]`,
    );
    card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    quickFindHighlightTimer = setTimeout(() => {
      highlightedOrderId.value = null;
      quickFindHighlightTimer = null;
    }, 3000);
  };

  const onQuickFindClosed = () => {
    if (pendingQuickFindOrderId.value === null) return;
    const orderId = pendingQuickFindOrderId.value;
    pendingQuickFindOrderId.value = null;
    if (filteredOrderList.value.some((order) => order.orderId === orderId)) {
      void focusQuickOrder(orderId);
    } else {
      clearQuickFind();
    }
  };

  const findQuickOrder = () => {
    if (!/^\d{4}$/.test(quickFindInput.value)) {
      showToast('请输入订单号后四位数字');
      return;
    }
    const matches = filteredOrderList.value.filter(
      (order) => order.orderId !== undefined && order.orderNo?.slice(-4) === quickFindInput.value,
    );
    if (matches.length === 0) {
      showToast('未找到订单，请检查尾号或当前筛选条件');
      return;
    }
    const orderId = matches[0].orderId;
    if (orderId === undefined) return;
    clearQuickFind();
    pendingQuickFindOrderId.value = orderId;
    quickFindVisible.value = false;
  };

  // 方法
  const formatDateTime = (timestamp?: string | Date) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    return `${month}-${day} ${hour}:${minute}`;
  };

  const getElapsedMinutes = (timestamp?: string | Date): number | null => {
    if (!timestamp) return null;
    const createdAtMs = new Date(timestamp).getTime();
    if (Number.isNaN(createdAtMs)) return null;
    return Math.max(0, Math.floor((currentTimeMs.value - createdAtMs) / 60000));
  };

  const getElapsedTimeClass = (timestamp?: string | Date) => {
    const minutes = getElapsedMinutes(timestamp);
    if (minutes !== null && minutes > 15) return 'elapsed-danger';
    if (minutes !== null && minutes > 10) return 'elapsed-warning';
    return '';
  };

  const foodDisplayName = (item: FoodList): string => {
    const spec = item.foodSpecName?.trim();
    return spec && spec !== '标准' ? `${item.foodName}（${spec}）` : item.foodName;
  };

  const isFoodCancelled = (item: FoodList): boolean => Number(item.status) === 999;

  const getActiveFoods = (order: OrderListItemModel): FoodList[] =>
    (order.foodList || []).filter((item) => !isFoodCancelled(item));

  const getOrderStatus = (order: OrderListItemModel) => {
    if (!order.foodList || order.foodList.length === 0) {
      return '状态: 待出餐';
    }

    const activeFoods = getActiveFoods(order);
    const totalItems = activeFoods.length;
    const completedItems = activeFoods.filter((item) => item.status === 9).length;

    if (totalItems === 0) return '状态: 菜品已全部取消';

    if (completedItems === 0) {
      return '状态: 待出餐';
    } else if (completedItems === totalItems) {
      return '状态: 已出餐';
    } else {
      return `状态: 部分出餐 (${completedItems}/${totalItems})`;
    }
  };

  // 新增：获取用餐类型显示文本
  const getDineTypeText = (order: OrderListItemModel) => {
    const dineTypeMap = {
      1: '堂食',
      2: '自提',
      3: '外卖',
      4: '打包',
      5: '预定',
    };
    return dineTypeMap[order.dineType as keyof typeof dineTypeMap] || '';
  };

  const getDineTypeClass = (order: OrderListItemModel) => {
    const dineTypeClassMap = {
      1: 'dine-in',
      2: 'self-pickup',
      3: 'delivery',
      4: 'packed',
      5: 'reservation',
    };
    return dineTypeClassMap[order.dineType as keyof typeof dineTypeClassMap] || '';
  };

  // 新增：判断是否是自提订单（根据tableTag）
  const isSelfPickupOrder = (order: OrderListItemModel) => {
    return order.dineType === 2 || Boolean(order.tableTag && order.tableTag.includes('自提'));
  };

  const isReservationOrder = (order: OrderListItemModel) => {
    return order.dineType === 5;
  };

  const isConvertedReservationOrder = (order: OrderListItemModel) => {
    return order.tableTag === '预定' && order.tableName !== '预定';
  };

  const getDisplayPickCode = (order: OrderListItemModel) => {
    if (
      isSelfPickupOrder(order) ||
      isReservationOrder(order) ||
      order.pickCode === order.orderNo?.slice(-4)
    ) {
      return '';
    }
    return order.pickCode || '';
  };

  const getOrderNoPrefix = (orderNo?: string) => {
    return orderNo?.slice(0, -4) || '';
  };

  const getOrderNoSuffix = (orderNo?: string) => {
    return orderNo?.slice(-4) || '';
  };

  const isDeliveryOrder = (order: OrderListItemModel) => {
    return order.dineType === 3;
  };

  const shouldShowAppointmentTime = (order: OrderListItemModel) => {
    return (
      isSelfPickupOrder(order) ||
      isDeliveryOrder(order) ||
      isReservationOrder(order) ||
      isConvertedReservationOrder(order)
    );
  };

  const getAppointmentTimeLabel = (order: OrderListItemModel) => {
    if (isDeliveryOrder(order)) return '外卖';
    if (isReservationOrder(order) || isConvertedReservationOrder(order)) return '预定';
    return '自提';
  };

  // 新增：判断是否显示发送到厨房按钮（预约订单且未确认）
  const shouldShowSendToKitchen = (order: OrderListItemModel) => {
    return (
      (isSelfPickupOrder(order) || isDeliveryOrder(order) || isReservationOrder(order)) &&
      order.orderStatus === 1
    );
  };

  // 新增：解析预约时间并检查是否需要确认
  const checkSelfPickupTime = (order: OrderListItemModel): boolean => {
    try {
      const selfPickDate = order.selfPickDate || '';
      const selfPickTime = order.selfPickTime || '';
      const appointmentLabel = getAppointmentTimeLabel(order);

      // 如果没有预约时间信息，阻止操作并提示
      if (!selfPickDate || !selfPickTime) {
        showToast(`没有${appointmentLabel}时间，无法发送到厨房`);
        return false;
      }

      // 解析自提时间段，例如 "17:00-18:00"
      const timeParts = selfPickTime.split('-');
      if (timeParts.length !== 2) {
        console.warn(`${appointmentLabel}时间格式无效:`, selfPickTime);
        showToast(`${appointmentLabel}时间格式无效，无法发送到厨房`);
        return false;
      }

      const startTime = timeParts[0].trim(); // 开始时间 "17:00"
      const endTime = timeParts[1].trim(); // 结束时间 "18:00"

      // 组合成完整的日期时间字符串
      const startDateTimeStr = `${selfPickDate} ${startTime}`;
      const endDateTimeStr = `${selfPickDate} ${endTime}`;

      // 解析为Date对象
      const startDateTime = new Date(startDateTimeStr);
      const endDateTime = new Date(endDateTimeStr);

      // 检查日期是否有效
      if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
        console.warn(`${appointmentLabel}时间格式无效:`, startDateTimeStr, endDateTimeStr);
        showToast(`${appointmentLabel}时间格式无效，无法发送到厨房`);
        return false;
      }

      // 获取当前时间
      const now = new Date();

      // 计算最早允许操作时间：开始时间前2小时
      const earliestAllowedTime = new Date(startDateTime.getTime() - 2 * 60 * 60 * 1000);

      const fullTimeDisplay = `${selfPickDate} ${selfPickTime}`;

      // 如果当前时间早于最早允许时间（提早超过2小时）
      if (now < earliestAllowedTime) {
        const confirmed = confirm(
          `是否确定要发送到厨房？\n\n${appointmentLabel}时间为：${fullTimeDisplay}\n\n注意：距离${appointmentLabel}时间还有超过2小时`,
        );
        return confirmed;
      }

      // 如果当前时间晚于结束时间（已过期）
      if (now > endDateTime) {
        const confirmed = confirm(
          `是否确定要发送到厨房？\n\n${appointmentLabel}时间为：${fullTimeDisplay}\n\n注意：此时间已过期`,
        );
        return confirmed;
      }

      // 当前时间在正常范围内：[开始时间-2小时, 结束时间]
      return true;
    } catch (error) {
      console.error('检查预约时间失败:', error);
      showToast('检查预约时间失败，请稍后重试');
      return false;
    }
  };

  const isSpecialTable = (table: OrderTableListItemModel) => {
    const tableTag = table.tableTag || '';
    return ['自提', '外卖', '预定'].some((specialTag) => tableTag.includes(specialTag));
  };

  const closeReservationTableModal = () => {
    if (isChangingReservationTable.value) return;
    reservationTableModalVisible.value = false;
    currentReservationOrder.value = null;
    availableReservationTables.value = [];
    selectedReservationTableId.value = null;
    isLoadingReservationTables.value = false;
  };

  const openReservationTableModal = async (order: OrderListItemModel) => {
    if (!order.shopId) {
      showToast('订单缺少餐厅信息，无法选择桌号');
      return;
    }

    currentReservationOrder.value = order;
    availableReservationTables.value = [];
    selectedReservationTableId.value = null;
    reservationTableModalVisible.value = true;
    isLoadingReservationTables.value = true;

    try {
      const response = await fetchTableList({
        pageNum: 1,
        pageSize: 200,
        data: { shopId: order.shopId },
      });
      if (response.code !== 200) {
        showToast(response.msg || '加载桌位失败');
        return;
      }

      availableReservationTables.value = (response.result.list || []).filter(
        (table) =>
          table.tableStatus === 0 &&
          !table.orderId &&
          Boolean(table.tableId) &&
          !isSpecialTable(table),
      );
    } catch (error) {
      console.error('加载预定订单可用桌位失败:', error);
      showToast('加载桌位失败，请稍后重试');
    } finally {
      isLoadingReservationTables.value = false;
    }
  };

  const confirmReservationTable = async () => {
    const order = currentReservationOrder.value;
    const tableId = selectedReservationTableId.value;
    if (!order?.orderId || !tableId || isChangingReservationTable.value) return;

    isChangingReservationTable.value = true;
    try {
      const response = await editOrderTable({
        orderId: order.orderId,
        tableId,
        dineType: 1,
      });
      if (response.code !== 0 && response.code !== 200) {
        showToast(response.msg || '订单转换失败');
        return;
      }

      let auditErrorMessage = '';
      try {
        const auditResponse = await auditOrder({ orderId: order.orderId });
        if (auditResponse.code !== 200) {
          auditErrorMessage = auditResponse.msg || '发送到厨房失败';
        }
      } catch (error) {
        console.error('预定订单发送到厨房失败:', error);
        auditErrorMessage = '网络异常，请稍后重试';
      }

      isChangingReservationTable.value = false;
      closeReservationTableModal();
      if (selectedShopId.value) {
        await fetchOrderList(selectedShopId.value);
      }
      if (auditErrorMessage) {
        showToast(`订单已转为普通订单，但发送到厨房失败：${auditErrorMessage}`);
      } else {
        showToast('订单已转为普通订单并发送到厨房');
      }
    } catch (error) {
      console.error('预定订单转换失败:', error);
      showToast('订单转换失败，请稍后重试');
    } finally {
      isChangingReservationTable.value = false;
    }
  };

  // 新增：发送到厨房（确认订单）
  const sendToKitchen = async (order: OrderListItemModel) => {
    if (!order.orderId) {
      showToast('订单信息不完整，无法发送到厨房');
      return;
    }

    // 检查自提时间，如果需要确认但用户取消，则直接返回
    if (!checkSelfPickupTime(order)) {
      return;
    }

    if (isReservationOrder(order)) {
      await openReservationTableModal(order);
      return;
    }

    try {
      const response = await auditOrder({ orderId: order.orderId });
      if (response.code === 200) {
        showToast(`订单 #${order.orderNo} 已发送到厨房！`);
        // 刷新订单列表
        if (selectedShopId.value) {
          await fetchOrderList(selectedShopId.value as number);
        }
      } else {
        showToast(response.msg || '发送到厨房失败');
      }
    } catch (error) {
      console.error('发送到厨房失败:', error);
      showToast('发送到厨房失败，请稍后重试');
    }
  };

  // 新增：格式化自提时间显示
  const formatSelfPickupTime = (order: OrderListItemModel) => {
    const date = order.selfPickDate || '';
    const time = order.selfPickTime || '';
    if (!date && !time) return '';

    // 检查是否是今天
    if (date) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
        2,
        '0',
      )}-${String(today.getDate()).padStart(2, '0')}`;

      if (date === todayStr) {
        // 如果是今天，显示"今天"
        return time ? `今天 ${time}` : '今天';
      }
    }

    // 不是今天，正常显示日期
    if (date && time) return `${date} ${time}`;
    if (date) return date;
    if (time) return time;
    return '';
  };

  const toggleFilterMenu = () => {
    showFilterMenu.value = !showFilterMenu.value;
    showNavMenu.value = false; // 关闭导航菜单
  };

  const toggleNavMenu = () => {
    showNavMenu.value = !showNavMenu.value;
    showFilterMenu.value = false; // 关闭筛选菜单
  };

  const isFilterTypeSelected = (filter: OrderTypeFilter): boolean => {
    return selectedFilterTypes.value.includes(filter);
  };

  const toggleFilterType = (filter: OrderTypeFilter) => {
    if (isFilterTypeSelected(filter)) {
      selectedFilterTypes.value = selectedFilterTypes.value.filter(
        (selectedFilter) => selectedFilter !== filter,
      );
      return;
    }

    selectedFilterTypes.value = [...selectedFilterTypes.value, filter];
  };

  const toggleAllFilterTypes = () => {
    selectedFilterTypes.value = isAllFilterTypesSelected.value
      ? []
      : orderTypeFilterOptions.map((option) => option.value);
  };

  const refreshOrders = async () => {
    if (selectedShopId.value) {
      await fetchOrderList(selectedShopId.value as number);
      showToast('订单已刷新');
    }
  };

  // 新增：排除未支付开关变化时，重新拉取订单
  const onExcludeUnPaidChange = async () => {
    if (selectedShopId.value) {
      await fetchOrderList(selectedShopId.value as number);
      updateRefreshTime();
    }
  };

  // 新增：更新刷新时间
  const updateRefreshTime = () => {
    const now = new Date();
    currentTimeMs.value = now.getTime();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    lastRefreshTime.value = `${hours}:${minutes}:${seconds}`;
  };

  // 新增：启动自动刷新定时器
  const startAutoRefresh = () => {
    // 清除现有定时器
    if (autoRefreshTimer) {
      clearInterval(autoRefreshTimer);
    }

    // 设置10秒定时器
    autoRefreshTimer = setInterval(async () => {
      if (selectedShopId.value !== null) {
        await fetchOrderList(selectedShopId.value as number);
        updateRefreshTime();
        console.log('自动刷新完成:', lastRefreshTime.value);
      }
    }, 10000); // 10秒
  };

  // 新增：停止自动刷新定时器
  const stopAutoRefresh = () => {
    if (autoRefreshTimer) {
      clearInterval(autoRefreshTimer);
      autoRefreshTimer = null;
    }
  };

  // 新增：判断是否可以显示确认支付完成按钮
  const isOrderPaid = (order: Pick<OrderListItemModel, 'paymentStatus' | 'paymentStatusName'>) => {
    if (order.paymentStatus === 10) return true;

    const statusLower = order.paymentStatusName?.toLowerCase() || '';
    return (
      statusLower.includes('已支付') ||
      statusLower.includes('支付成功') ||
      statusLower.includes('paid') ||
      statusLower.includes('success')
    );
  };

  const canShowConfirmPayment = (order: OrderListItemModel) => {
    // 如果订单已取消，不能确认支付
    if (order.orderStatus === 999) {
      return false;
    }

    if (isOrderPaid(order)) {
      return false;
    }

    const payableAmount = order.remainAmount ?? order.totalAmount ?? 0;
    return payableAmount > 0;
  };

  // 新增：加载店铺详情并判断支付方式
  const loadShopDetail = async (shopId: number) => {
    try {
      const response = await fetchShopDetail({ shopId: shopId });
      if (response.code === 200) {
        // 判断支付方式：0线下、1线上
        isOnlinePayment.value = response.result.paymentLine === 1;
        // 如果有B扫C收款选项，默认选择B扫C收款
        if (isOnlinePayment.value) {
          selectedPaymentMethod.value = 3;
        }
      }
    } catch (error) {
      console.error('加载店铺详情失败:', error);
    }
  };

  // 新增：显示支付确认模态框
  const showPaymentConfirmModal = async (order: any) => {
    // 保存当前订单信息到本地状态，供确认支付时使用
    currentPaymentOrder.value = order;

    // 加载店铺详情以判断支付方式
    if (order.shopId) {
      await loadShopDetail(order.shopId);
    }

    // 如果有B扫C收款选项，默认选择B扫C收款，否则选择POS支付
    selectedPaymentMethod.value = isOnlinePayment.value ? 3 : 4;
    paymentRemark.value = '';
    paymentConfirmModalVisible.value = true;
  };

  // 新增：隐藏支付确认模态框
  const hidePaymentConfirmModal = () => {
    paymentConfirmModalVisible.value = false;
    isConfirmingPayment.value = false;
    // 如果有B扫C收款选项，默认选择B扫C收款，否则选择POS支付
    selectedPaymentMethod.value = isOnlinePayment.value ? 3 : 4;
    paymentRemark.value = '';
    currentPaymentOrder.value = null;
  };

  // 处理支付确认（根据支付方式选择不同的处理逻辑）
  const handlePaymentConfirm = () => {
    if (selectedPaymentMethod.value === 3) {
      // B扫C收款，显示扫码界面
      paymentConfirmModalVisible.value = false;
      isConfirmingPayment.value = false;
      paymentRemark.value = '';
      showScanPaymentModal();
    } else {
      // 其他支付方式，直接确认支付
      confirmPayment();
    }
  };

  const isPaymentPrintFailure = (message?: string): boolean => {
    if (!message) return false;
    return /打印|打印机|小票/.test(message);
  };

  const getPaymentErrorMessage = (error: unknown): string => {
    if (error instanceof Error && isPaymentPrintFailure(error.message)) {
      return error.message;
    }

    if (typeof error !== 'object' || error === null || !('response' in error)) {
      return '';
    }

    const response = (error as { response?: { data?: { msg?: unknown } } }).response;
    return typeof response?.data?.msg === 'string' ? response.data.msg : '';
  };

  const refreshOrderListAfterPayment = async () => {
    if (!selectedShopId.value) return;

    try {
      await fetchOrderList(selectedShopId.value as number);
    } catch (error) {
      console.error('支付完成后刷新订单列表失败:', error);
    }
  };

  const verifyOrderPaid = async (orderId: number): Promise<boolean | null> => {
    try {
      const response = await getOrder({ orderId });
      if (response.code !== 200 || !response.result) return null;
      return isOrderPaid(response.result);
    } catch (error) {
      console.error('核验订单支付状态失败:', error);
      return null;
    }
  };

  const handlePaymentSubmissionError = async (
    orderId: number,
    errorMessage: string,
  ): Promise<void> => {
    const paymentConfirmed = await verifyOrderPaid(orderId);

    if (paymentConfirmed === true) {
      hidePaymentConfirmModal();
      showToast('支付已成功，但打印失败，请稍后补打小票');
      await refreshOrderListAfterPayment();
      return;
    }

    if (paymentConfirmed === null) {
      hidePaymentConfirmModal();
      showToast('支付结果暂时无法确认，请刷新订单状态后再操作');
      return;
    }

    showToast(errorMessage || '确认支付失败');
  };

  // 新增：确认支付
  const confirmPayment = async () => {
    if (isConfirmingPayment.value) return;

    const currentOrder = currentPaymentOrder.value;
    if (!currentOrder || !currentOrder.orderId) {
      showToast('订单信息不完整，无法确认支付');
      return;
    }

    isConfirmingPayment.value = true;

    try {
      // 获取工号
      const employeeId = getEmployeeId();
      const employeeName = getEmployeeName();

      let remark = '';
      let paymentMethodName = '';

      // 根据选择的支付方式设置备注和支付方式名称
      if (selectedPaymentMethod.value === 3) {
        paymentMethodName = 'B扫C收款';
        remark = `服务员${employeeName}确认B扫C收款完成，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 4) {
        paymentMethodName = 'POS支付';
        remark = `服务员${employeeName}确认POS支付完成，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 5) {
        paymentMethodName = '公司挂账';
        remark = `服务员${employeeName}确认挂账，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 7) {
        paymentMethodName = '现金支付';
        remark = `服务员${employeeName}确认现金支付完成，${paymentRemark.value || ''}`;
      }

      // 构建支付数据
      const paymentData = {
        orderId: currentOrder.orderId,
        paymentMethod: selectedPaymentMethod.value,
        paymentMethodName: paymentMethodName,
        paymentStatus: 1, // 已支付
        amount: currentOrder.remainAmount ?? currentOrder.totalAmount ?? 0,
        paymentTime: dateTimeFormat(dayjs()),
        remark: remark.trim(),
      };

      // 调用支付确认接口
      const response = await insertOrderPayment(paymentData);
      if (response.code === 200) {
        hidePaymentConfirmModal();
        showToast(`${currentOrder.tableName}号桌${paymentMethodName}已确认完成！`);
        await refreshOrderListAfterPayment();
      } else if (isPaymentPrintFailure(response.msg)) {
        await handlePaymentSubmissionError(currentOrder.orderId, response.msg);
      } else {
        showToast(response.msg || '确认支付失败');
      }
    } catch (error) {
      console.error('确认支付失败:', error);
      const errorMessage = getPaymentErrorMessage(error);
      if (isPaymentPrintFailure(errorMessage)) {
        await handlePaymentSubmissionError(currentOrder.orderId, errorMessage);
      } else {
        showToast('确认支付失败，请稍后重试');
      }
    } finally {
      isConfirmingPayment.value = false;
    }
  };

  // 从登录态获取工号
  const getEmployeeId = () => {
    return employeeId.value;
  };

  // 从登录态获取员工姓名
  const getEmployeeName = () => {
    return employeeName.value;
  };

  const showToast = (message: string) => {
    // 创建提示框
    const toast = document.createElement('div');
    toast.className =
      'fixed top-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-lg z-50 transform translate-x-full transition-transform duration-300';
    toast.textContent = message;
    document.body.appendChild(toast);

    // 显示动画
    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);

    // 自动隐藏
    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 2000);
  };

  // 原有的接口调用逻辑
  // const foodOut = async (shopId: number, orderId: number, orderFoodId: number) => {
  //   return await outFood({
  //     orderId: orderId,
  //     orderFoodId: orderFoodId,
  //     shopId: shopId,
  //   });
  // };

  const fetchOrderList = async (shopId: number) => {
    const requestData: any = {
      shopId: shopId,
      orderStatuss: [1, 2, 8],
      sortType: 2,
    };

    // 如果勾选“排除未支付”，增加支付状态筛选条件（仅查询已支付，例如状态10）
    if (excludeUnPaid.value) {
      requestData.paymentStatuss = [10];
    }

    const respOrder = await getOrderList({
      pageNum: 1,
      pageSize: 999,
      data: requestData,
    });
    if (respOrder.code === 200) {
      const list = respOrder.result.list || [];
      // list[0].dineType = 2;
      orderList.value = list.map((order) => ({
        ...order,
        foodList: [...(order.foodList || [])].sort(
          (a, b) =>
            (a.orderFoodId ?? Number.MAX_SAFE_INTEGER) - (b.orderFoodId ?? Number.MAX_SAFE_INTEGER),
        ),
      }));

      // 新增：记录最后一项创建时间并与上次记录对比，提示新订单
      if (list.length > 0) {
        const last = list[list.length - 1];
        const lastCreateMs = new Date((last as any).createTime || '').getTime();
        if (!Number.isNaN(lastCreateMs)) {
          const prev = getStoredLastCreateTime(shopId);
          if (prev !== null && lastCreateMs > prev) {
            showToast('您有新的订单，请注意查收');
            playNewOrderSound();
          }
          setStoredLastCreateTime(shopId, lastCreateMs);
        }
      }
    }
  };

  const onShopChange = async () => {
    if (selectedShopId.value !== null) {
      // 保存选中的餐厅到localStorage
      saveSelectedShop(selectedShopId.value as number);
      await fetchOrderList(selectedShopId.value as number);
    }
  };

  const removeOrder = (orderId: number) => {
    const idx = orderList.value.findIndex((o) => o.orderId === orderId);
    if (idx !== -1) orderList.value.splice(idx, 1);
  };

  // 从接口返回中安全获取订单状态
  const getOrderStatusFromResp = (resp: any): number | null => {
    if (!resp) return null;
    if (typeof resp.orderStatus === 'number') return resp.orderStatus;
    if (resp.result && typeof resp.result.orderStatus === 'number') return resp.result.orderStatus;
    return null;
  };

  const handleFoodOut = async (order: any, item: any) => {
    if (item.status === 9 || isFoodCancelled(item)) return;
    console.log('item:', item);
    console.log(
      'orderId:',
      order.orderId,
      'orderFoodId:',
      item.orderFoodId,
      'shopId:',
      order.shopId,
    );
    const resp: any = await foodUp({
      orderId: order.orderId,
      orderFoodId: item.orderFoodId,
      shopId: order.shopId,
    });
    if (resp.code === 200) {
      const idx = order.foodList.findIndex((f: any) => f.orderFoodId === item.orderFoodId);
      if (idx !== -1) {
        order.foodList[idx] = { ...order.foodList[idx], status: 9 }; // 更新为已上菜状态
      }

      // 根据接口返回的订单状态决定是否移除订单
      const orderStatus = getOrderStatusFromResp(resp);
      if (orderStatus === 10) {
        setTimeout(() => {
          removeOrder(order.orderId);
        }, 300);
      }
    }
  };

  // 新增：判断订单是否全部出餐
  const isOrderAllCompleted = (order: OrderListItemModel): boolean => {
    const activeFoods = getActiveFoods(order);
    return activeFoods.length > 0 && activeFoods.every((item) => item.status === 9);
  };

  // 新增：核销按钮点击，调用订单完成接口
  const handleVerify = async (order: any) => {
    if (!order?.orderId) {
      console.warn('核销失败：缺少订单ID');
      return;
    }
    try {
      const resp = await completeOrder({ orderId: order.orderId });
      if (resp.code === 200) {
        // 接口成功后，从列表中移除该订单
        removeOrder(order.orderId);
      } else {
        console.warn('核销失败:', resp.msg || '接口返回错误');
      }
    } catch (e) {
      console.error('核销异常:', e);
    }
  };

  const completeAllFood = async (orderId: number, shopId: number) => {
    return await foodUp({
      orderId: orderId,
      shopId: shopId,
    });
  };

  const handleCompleteAll = async (order: any) => {
    if (getActiveFoods(order).length === 0) return;
    const resp: any = await completeAllFood(order.orderId, order.shopId);
    if (resp.code === 200) {
      // 标记所有菜品为已出餐
      if (order.foodList) {
        order.foodList.forEach((item: FoodList) => {
          if (!isFoodCancelled(item)) item.status = 9;
        });
      }

      // 根据接口返回的订单状态决定是否移除订单
      const orderStatus = getOrderStatusFromResp(resp);
      if (orderStatus === 10) {
        setTimeout(() => {
          removeOrder(order.orderId);
        }, 300);
      }
    }
  };

  const goToDishAssignment = () => {
    router.push('/dishAssignment');
    showNavMenu.value = false; // 关闭菜单
  };

  const goToKitchenOrder = () => {
    router.push('/kitchenOrder');
    showNavMenu.value = false; // 关闭菜单
  };

  const goToHistory = () => {
    router.push('/servingHistory');
    showNavMenu.value = false; // 关闭菜单
  };

  const goToOrderHistory = () => {
    router.push('/orderHistory');
    showNavMenu.value = false; // 关闭菜单
  };

  const goToTableManage = () => {
    router.push('/tableManage');
    showNavMenu.value = false; // 关闭菜单
  };

  const goToFoodManage = () => {
    router.push('/foodManage');
    showNavMenu.value = false; // 关闭菜单
  };

  // 点击外部关闭菜单
  const handleClickOutside = (event: Event) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-dropdown')) {
      showFilterMenu.value = false;
    }
    if (!target.closest('.nav-dropdown')) {
      showNavMenu.value = false;
    }
  };

  onMounted(async () => {
    const respShop = await fetchShopList({ pageNum: 1, pageSize: 100, data: { shopStatus: 0 } });
    if (respShop.code === 200 && respShop.result.list.length > 0) {
      shopList.value = respShop.result.list;
      const savedShopId = loadSelectedShop();
      if (savedShopId && isValidShop(savedShopId)) {
        selectedShopId.value = savedShopId;
      } else {
        selectedShopId.value = shopList.value[0].shopId;
        // 确保selectedShopId.value不为null时再保存
        if (selectedShopId.value !== null) {
          saveSelectedShop(selectedShopId.value as number);
        }
      }
      // 确保selectedShopId.value不为null时再加载订单
      if (selectedShopId.value !== null) {
        await fetchOrderList(selectedShopId.value as number);
      }

      // 新增：初始化刷新时间并启动自动刷新
      updateRefreshTime();
      startAutoRefresh();
      // 新增：初始化新订单提示音
      initNewOrderAudio();
    }

    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    clearQuickFind();
    document.removeEventListener('click', handleClickOutside);
    // 新增：组件卸载时清理定时器
    stopAutoRefresh();
    newOrderAudio = null;
    // 清理扫码资源
    stopScan();
  });

  // 显示扫码支付全屏界面
  const showScanPaymentModal = async () => {
    scanPaymentModalVisible.value = true;
    scanPaymentRemark.value = '';
    scanStatus.value = '';
    // 立即设置为扫描中，避免显示"正在启动摄像头"提示
    isScanning.value = true;

    // 自动开始扫描
    await nextTick();
    getCamerasInfo();
  };

  // 隐藏扫码支付全屏界面
  const hideScanPaymentModal = () => {
    stopScan();
    scanPaymentModalVisible.value = false;
    isSubmittingScanPayment.value = false;
    scanPaymentRemark.value = '';
    scanStatus.value = '';
  };

  // 获取摄像头信息并初始化
  const getCamerasInfo = () => {
    // 立即设置为扫描中，隐藏"正在启动摄像头"提示
    isScanning.value = true;

    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length) {
          html5QrCode = new Html5Qrcode('qr-reader');
          startScan();
        } else {
          // 没有可用摄像头
          isScanning.value = false;
          scanStatus.value = '未检测到摄像头设备';
          scanStatusClass.value = 'bg-red-100 text-red-700';
        }
      })
      .catch((err) => {
        console.error('获取摄像头失败:', err);
        scanStatus.value = '摄像头无访问权限！';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        isScanning.value = false;
      });
  };

  // 开始扫描
  const startScan = () => {
    if (!html5QrCode) {
      console.error('html5QrCode 未初始化');
      return;
    }

    isScanning.value = true;
    scanStatus.value = '';
    scanStatusClass.value = '';

    html5QrCode
      .start(
        { facingMode: 'environment' },
        {
          fps: 20,
          // 全屏扫码：使用整个视窗大小
          qrbox: function (viewfinderWidth, viewfinderHeight) {
            return {
              width: viewfinderWidth,
              height: viewfinderHeight,
            };
          },
          // 不限制宽高比，允许全屏显示
          aspectRatio: undefined,
        },
        (decodedText, decodedResult) => {
          // 扫码成功回调
          handleScanResult(decodedText);
        },
      )
      .then(() => {
        // 扫描启动成功后，强制应用全屏样式
        nextTick(() => {
          forceFullscreenStyles();
        });
      })
      .catch((err) => {
        console.error('扫码失败', err);
        scanStatus.value = '启动摄像头失败，请检查权限设置';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        isScanning.value = false;
      });
  };

  // 强制应用全屏样式
  const forceFullscreenStyles = () => {
    const qrReader = document.getElementById('qr-reader');
    if (!qrReader) return;

    // 强制设置容器样式
    const container = qrReader.querySelector('#html5qr-container');
    if (container) {
      (container as HTMLElement).style.width = '100%';
      (container as HTMLElement).style.height = '100%';
      (container as HTMLElement).style.minWidth = '100%';
      (container as HTMLElement).style.minHeight = '100%';
    }

    // 强制设置视频样式
    const videos = qrReader.querySelectorAll('video');
    videos.forEach((video) => {
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.minWidth = '100%';
      video.style.minHeight = '100%';
      video.style.objectFit = 'cover';
      video.style.position = 'absolute';
      video.style.top = '0';
      video.style.left = '0';
      video.style.right = '0';
      video.style.bottom = '0';
    });

    // 强制设置画布样式
    const canvases = qrReader.querySelectorAll('canvas');
    canvases.forEach((canvas) => {
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.minWidth = '100%';
      canvas.style.minHeight = '100%';
      canvas.style.objectFit = 'cover';
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.right = '0';
      canvas.style.bottom = '0';
    });
  };

  // 停止扫描
  const stopScan = async (): Promise<void> => {
    if (html5QrCode && html5QrCode.isScanning) {
      try {
        await html5QrCode.stop();
        html5QrCode.clear();
      } catch (error) {
        console.error('停止扫描失败:', error);
      }
    }
    isScanning.value = false;
  };

  // 处理扫描结果
  const handleScanResult = async (scanCode: string) => {
    // 验证扫码结果
    if (!scanCode || scanCode.trim() === '' || isSubmittingScanPayment.value) {
      return;
    }

    isSubmittingScanPayment.value = true;

    try {
      // 停止扫描，但保持界面打开
      await stopScan();

      // 显示处理中状态
      scanStatus.value = '扫码成功，正在处理支付...';
      scanStatusClass.value = 'bg-blue-100 text-blue-700';

      // 调用支付接口
      await submitScanPayment(scanCode);
    } finally {
      isSubmittingScanPayment.value = false;
    }
  };

  // 提交扫码支付
  const submitScanPayment = async (scanCode: string) => {
    try {
      const currentOrder = currentPaymentOrder.value;
      if (!currentOrder || !currentOrder.orderId) {
        scanStatus.value = '订单信息不完整，无法支付';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        showToast('订单信息不完整，无法支付');
        return;
      }

      // 获取IP地址（简化处理，实际可以从服务器获取）
      const ip = await getClientIP();

      // 构建支付数据
      const paymentData = {
        orderId: currentOrder.orderId,
        scanCode: scanCode,
        remark: scanPaymentRemark.value || '',
        ip: ip || '',
        amount: currentOrder.remainAmount ?? currentOrder.totalAmount ?? 0,
      };

      // 调用扫码支付接口
      const response = await scanPayment(paymentData);

      // 根据接口返回结果处理
      if (response.code === 200) {
        // API 返回 200，立即关闭扫码界面
        hideScanPaymentModal();

        // 根据业务结果在详情界面显示提示
        if (response.result?.success) {
          // 支付成功
          showToast('扫码支付成功！');
          // 刷新订单列表
          if (selectedShopId.value) {
            await fetchOrderList(selectedShopId.value as number);
          }
        } else {
          // 支付失败，显示确认模态框
          scanPaymentFailErrorMsg.value = response.result?.errorMsg || '';
          scanPaymentFailModalVisible.value = true;
        }
      } else {
        // API 返回非 200，支付结果可能不明确，交由服务员选择查询或重新扫码
        const errorMsg = response.msg || '支付失败，请重试';
        hideScanPaymentModal();
        scanPaymentFailErrorMsg.value = errorMsg;
        scanPaymentFailModalVisible.value = true;
      }
    } catch (error) {
      console.error('扫码支付失败:', error);

      // 网络异常时支付结果也可能不明确，交由服务员选择查询或重新扫码
      hideScanPaymentModal();
      scanPaymentFailErrorMsg.value = '支付失败，请稍后重试';
      scanPaymentFailModalVisible.value = true;
    }
  };

  // 获取客户端IP地址（简化版本）
  const getClientIP = async (): Promise<string> => {
    // 可以调用后端接口获取真实IP，这里先返回空字符串
    // 或者使用第三方服务获取IP
    return '';
  };

  // 确认已经支付（刷新扫码支付状态）
  const confirmScanPaymentPaid = async () => {
    // 防抖：如果正在刷新，直接返回
    if (isRefreshingPayment.value) {
      return;
    }

    try {
      const currentOrder = currentPaymentOrder.value;
      if (!currentOrder || !currentOrder.orderId) {
        showToast('订单信息不完整，无法确认支付');
        return;
      }

      // 设置 loading 状态
      isRefreshingPayment.value = true;

      const response = await refreshScanPayment({
        orderId: currentOrder.orderId,
      });

      if (response.code === 200) {
        // 根据业务结果处理
        if (response.result?.success) {
          // 支付成功，关闭模态框
          scanPaymentFailModalVisible.value = false;
          scanPaymentFailErrorMsg.value = '';
          showToast('支付成功！');
          // 刷新订单列表
          if (selectedShopId.value) {
            await fetchOrderList(selectedShopId.value as number);
          }
        } else {
          // 支付失败，显示错误信息，模态框不关闭
          const errorMsg = response.result?.errorMsg || '刷新支付状态失败，请重试';
          scanPaymentFailErrorMsg.value = errorMsg;
          showToast(errorMsg);
        }
      } else {
        // API 返回非 200，显示错误信息，模态框不关闭
        const errorMsg = response.msg || '刷新支付状态失败，请稍后重试';
        scanPaymentFailErrorMsg.value = errorMsg;
        showToast(errorMsg);
      }
    } catch (error) {
      console.error('刷新支付状态失败:', error);
      const errorMsg = '刷新支付状态失败，请稍后重试';
      scanPaymentFailErrorMsg.value = errorMsg;
      showToast(errorMsg);
    } finally {
      // 无论成功失败，都要重置 loading 状态
      isRefreshingPayment.value = false;
    }
  };

  // 确认未支付后重新扫码，重新发起扫码支付
  const retryScanPayment = async () => {
    scanPaymentFailModalVisible.value = false;
    scanPaymentFailErrorMsg.value = '';
    await showScanPaymentModal();
  };

  // 取消确认支付
  const cancelScanPaymentConfirm = () => {
    scanPaymentFailModalVisible.value = false;
    scanPaymentFailErrorMsg.value = '';
    isRefreshingPayment.value = false; // 重置 loading 状态
  };
</script>

<style>
  html,
  body {
    height: 100vh;
    min-height: 100vh;
    width: 100%;
    margin: 0;
    padding: 0;
    background: none;
    overflow: hidden;
  }
</style>

<style scoped>
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overscroll-behavior: none;
    background-color: transparent;
  }

  /* 深色模式支持 */
  @media (prefers-color-scheme: dark) {
    .content-container {
      background-color: #000000;
      color: #ffffff;
    }

    .order-card {
      background-color: #1c1c1e;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    .restaurant-name,
    .order-id,
    .item-name {
      color: #ffffff;
    }

    .order-timestamp {
      color: #8e8e93;
    }

    .item-quantity {
      background-color: #2c2c2e;
    }

    .order-item {
      border-bottom: 1px solid #38383a;
    }

    .order-header {
      border-bottom: 1px solid #38383a;
    }

    .order-footer {
      border-top: 1px solid #38383a;
    }

    .filter-menu {
      background-color: #1c1c1e;
      border: 1px solid #38383a;
    }

    .filter-menu-item {
      color: #ffffff;
    }

    .filter-menu-section {
      border-bottom: 1px solid #38383a;
    }
  }

  .content-container {
    background-color: #f2f2f7;
    padding: 10px;
    color: #000000;
    background-image: url('@/assets/bg.jpg');
    background-size: 100% 100%;
    font-size: 18px;
    min-height: 100vh;
    height: 100vh;
    overflow-y: auto;
    box-sizing: border-box;
    width: 100%;
    min-width: 100vw;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 2px;
  }

  .title {
    font-size: 40px;
    font-weight: 700;
    color: #ffffff;
  }

  /* 餐厅选择器样式 */
  .restaurant-selector {
    position: relative;
    display: inline-block;
  }

  .restaurant-dropdown {
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    color: #ffffff;
    font-size: 20px;
    font-weight: 700;
    padding: 12px 60px 12px 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    min-width: 250px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
  }

  .restaurant-dropdown:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }

  .restaurant-dropdown:focus {
    outline: none;
    border-color: #007aff;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.3);
  }

  .restaurant-selector::after {
    content: '▼';
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #ffffff;
    font-size: 16px;
    pointer-events: none;
    transition: transform 0.3s ease;
  }

  .restaurant-selector:hover::after {
    transform: translateY(-50%) scale(1.1);
  }

  /* 下拉选项样式 */
  .restaurant-dropdown option {
    background-color: #1c1c1e;
    color: #ffffff;
    padding: 12px;
    font-size: 18px;
    font-weight: 500;
  }

  .restaurant-dropdown option:hover {
    background-color: #007aff;
  }

  .actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  .nav-buttons {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .nav-btn {
    background: #007aff;
    color: white;
    padding: 10px 16px;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 500;
    cursor: pointer;
  }

  .nav-btn:hover {
    background: #0056b3;
  }

  .filter-group {
    display: flex;
    gap: 8px;
    margin-right: 15px;
  }

  /* 视图切换按钮 */
  .view-toggle {
    display: flex;
    gap: 8px;
    margin-right: 15px;
    background-color: #f2f2f7;
    border-radius: 12px;
    padding: 4px;
  }

  .view-btn {
    background-color: transparent;
    color: #8e8e93;
    border: none;
    padding: 16px 28px;
    border-radius: 12px;
    font-size: 20px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    min-width: 140px;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .view-btn.active {
    background-color: #007aff;
    color: white;
  }

  .view-btn:hover {
    background-color: rgba(0, 122, 255, 0.1);
    color: #007aff;
  }

  /* 订单出餐样式 */
  .order-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 12px;
    padding-bottom: 12px;
  }

  .order-card {
    background-color: #ffffff;
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .order-card:hover {
    transform: translateY(-2px);
  }

  .order-card.quick-find-highlight {
    outline: 4px solid #facc15;
    outline-offset: 2px;
    background-color: #fffbeb;
  }

  .order-header {
    border-bottom: 1px solid #e5e5ea;
    padding-bottom: 8px;
    margin-bottom: 8px;
    text-align: left;
    min-width: 0;
  }

  /* 新增：自提订单头部样式 */
  .order-header.takeaway-header {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    border: 2px solid #f59e0b;
    border-radius: 12px 12px 0 0;
    padding: 10px;
    margin: -12px -12px 8px;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);
  }

  .order-header.takeaway-header .restaurant-name {
    color: #92400e;
    font-weight: 700;
  }

  .order-header.takeaway-header .order-timestamp {
    color: #92400e;
  }

  .order-header.takeaway-header .order-amount {
    color: #92400e;
    font-weight: 700;
  }

  .order-header.takeaway-header .order-id {
    color: #92400e;
  }

  .order-header.takeaway-header .order-table {
    color: #92400e;
    font-weight: 700;
  }

  .order-header.takeaway-header .send-to-kitchen-btn {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: white;
  }

  .order-header.takeaway-header .send-to-kitchen-btn:hover {
    background: linear-gradient(135deg, #16a34a, #15803d);
  }

  .order-header.takeaway-header .self-pickup-time {
    color: #92400e;
  }

  .order-header.takeaway-header .self-pickup-time i {
    color: #d97706;
  }

  .order-header.delivery-header {
    background: linear-gradient(135deg, #cffafe, #a5f3fc);
    border: 2px solid #06b6d4;
    border-radius: 12px 12px 0 0;
    padding: 10px;
    margin: -12px -12px 8px;
    box-shadow: 0 2px 8px rgba(6, 182, 212, 0.15);
  }

  .order-header.delivery-header .restaurant-name,
  .order-header.delivery-header .order-timestamp,
  .order-header.delivery-header .order-amount,
  .order-header.delivery-header .order-id,
  .order-header.delivery-header .order-table {
    color: #155e75;
  }

  .order-header.delivery-header .restaurant-name,
  .order-header.delivery-header .order-amount,
  .order-header.delivery-header .order-table {
    font-weight: 700;
  }

  .order-header.delivery-header .self-pickup-time {
    color: #155e75;
  }

  .order-header.delivery-header .self-pickup-time i {
    color: #0891b2;
  }

  .order-header.reservation-header {
    background: linear-gradient(135deg, #f3e8ff, #e9d5ff);
    border: 2px solid #a855f7;
    border-radius: 12px 12px 0 0;
    padding: 10px;
    margin: -12px -12px 8px;
    box-shadow: 0 2px 8px rgba(168, 85, 247, 0.15);
  }

  .order-header.reservation-header .restaurant-name,
  .order-header.reservation-header .order-timestamp,
  .order-header.reservation-header .order-amount,
  .order-header.reservation-header .order-id,
  .order-header.reservation-header .order-table {
    color: #6b21a8;
  }

  .order-header.reservation-header .restaurant-name,
  .order-header.reservation-header .order-amount,
  .order-header.reservation-header .order-table {
    font-weight: 700;
  }

  .order-type-badge {
    display: inline-flex;
    align-items: center;
    margin-left: 4px;
    padding: 2px 6px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.4;
    vertical-align: middle;
  }

  .order-type-badge.dine-in {
    background-color: #dcfce7;
    color: #166534;
  }

  .order-type-badge.self-pickup {
    background-color: #fef3c7;
    color: #92400e;
  }

  .order-type-badge.delivery {
    background-color: #ecfeff;
    color: #155e75;
  }

  .order-type-badge.packed {
    background-color: #f3f4f6;
    color: #374151;
  }

  .order-type-badge.reservation {
    background-color: #f3e8ff;
    color: #6b21a8;
  }

  /* 新增：自提订单操作行样式 */
  .self-pickup-action-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 6px;
    gap: 6px;
  }

  .send-to-kitchen-btn {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: white;
    border: none;
    padding: 6px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    white-space: nowrap;
    flex-shrink: 0;
    box-shadow: 0 2px 6px rgba(34, 197, 94, 0.25);
  }

  .send-to-kitchen-btn:hover {
    background: linear-gradient(135deg, #16a34a, #15803d);
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(34, 197, 94, 0.3);
  }

  .send-to-kitchen-btn:active {
    transform: scale(0.98);
  }

  .send-to-kitchen-btn i {
    font-size: 12px;
  }

  .self-pickup-time {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 500;
    color: #000000;
    white-space: nowrap;
  }

  .self-pickup-time i {
    font-size: 12px;
    color: #f59e0b;
    margin-right: 4px;
  }

  .restaurant-name {
    flex: 1;
    min-width: 0;
    font-size: 16px;
    font-weight: 600;
    color: #000000;
    margin-bottom: 2px;
    line-height: 1.35;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  .restaurant-name span {
    font-size: inherit;
  }

  .order-timestamp {
    font-size: 13px;
    color: #8e8e93;
    margin-bottom: 4px;
  }

  .order-amount {
    font-size: 14px;
    color: #000000;
    margin-bottom: 4px;
  }

  /* 新增：日期和金额行布局 */
  .order-timestamp-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
    gap: 8px;
  }

  .order-time-info {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 8px;
    min-width: 0;
  }

  .order-timestamp-row .order-timestamp {
    font-size: 13px;
    color: #8e8e93;
    margin-bottom: 0;
  }

  .order-elapsed-time {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
  }

  .order-elapsed-time.elapsed-warning {
    color: #ca8a04;
  }

  .order-elapsed-time.elapsed-danger {
    color: #dc2626;
  }

  .order-timestamp-row .order-amount {
    flex-shrink: 0;
    font-size: 14px;
    color: #000000;
    font-weight: 600;
    margin-bottom: 0;
  }

  .order-id {
    font-size: 12px;
    font-weight: 600;
    color: #000000;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .order-pay-status {
    flex-shrink: 0;
    font-size: 12px;
    margin-left: 4px;
  }

  .order-pay-status.unpaid {
    color: #ff3b30;
    font-weight: bold;
  }

  /* 新增：已支付状态显示为绿色 */
  .order-pay-status.paid {
    color: #34c759;
    font-weight: bold;
  }

  .order-table {
    font-size: 22px;
    font-weight: 600;
    color: #000000;
  }

  .order-reservation-count {
    margin-top: 2px;
    font-size: 14px;
    font-weight: 700;
    color: #6b21a8;
  }

  .delivery-address {
    display: flex;
    align-items: flex-start;
    margin-top: 2px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
    color: #155e75;
    word-break: break-word;
  }

  .delivery-address i {
    flex-shrink: 0;
    margin-top: 3px;
    color: #0891b2;
  }

  /* 新增：备注样式 */
  .order-remark {
    font-size: 16px;
    font-weight: 500;
    color: #dc2626;
    margin-top: 2px;
    display: flex;
    align-items: center;
  }

  .order-remark i {
    font-size: 12px;
    margin-right: 4px;
  }

  .order-items {
    margin-bottom: 8px;
    flex-grow: 1;
    min-width: 0;
  }

  .order-item {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 0;
    border-bottom: 1px solid #e5e5ea;
    align-items: center;
    position: relative;
  }

  .order-item-content {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .item-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
  }

  .item-name {
    max-width: 100%;
    font-size: 17px;
    font-weight: 500;
    color: #000000;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }

  /* 新增：口味标签样式 */
  .item-cook {
    max-width: 100%;
    font-size: 12px;
    color: #007aff;
    background-color: rgba(0, 122, 255, 0.1);
    padding: 1px 6px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 2px;
    border: 1px solid rgba(0, 122, 255, 0.2);
  }

  .item-cook i {
    font-size: 12px;
  }

  .item-quantity {
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 600;
    color: #ff3b30;
    background-color: #f2f2f7;
    padding: 2px 6px;
    border-radius: 10px;
    min-width: 34px;
    text-align: center;
  }

  .item-btn {
    flex: 0 0 64px;
    width: 64px;
    height: 38px;
    background-color: #007aff;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .item-btn:active {
    transform: scale(0.95);
    opacity: 0.9;
  }

  .item-btn:disabled {
    cursor: not-allowed;
    opacity: 0.8;
  }

  .order-item-cancelled {
    background-color: #f9fafb;
  }

  .order-item-cancelled .item-name,
  .order-item-cancelled .item-cook,
  .order-item-cancelled .item-quantity {
    color: #6b7280;
  }

  .order-item-cancelled .item-name,
  .order-item-cancelled .item-quantity {
    text-decoration: line-through;
  }

  .order-item-cancelled .item-cook {
    background-color: #f2f2f7;
    border-color: #e5e5ea;
  }

  .order-item-cancelled .item-btn {
    background-color: #e5e7eb;
    color: #4b5563;
    opacity: 1;
  }

  .order-footer {
    gap: 8px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e5e5ea;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .order-status {
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
  }

  .complete-all-btn {
    background-color: #007aff;
    color: white;
    border: none;
    flex-shrink: 0;
    min-height: 38px;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .complete-all-btn:active {
    transform: scale(0.98);
    opacity: 0.9;
  }

  /* 新增：核销按钮橙色样式 */
  .verify-btn {
    background-color: #ff9500;
  }

  .verify-btn:hover {
    background-color: #ff7f00;
  }

  .complete-btn {
    display: none;
  }

  .urgent-badge {
    background-color: #ff3b30;
    color: white;
    padding: 2px 8px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 500;
    margin-left: 8px;
  }

  .filter-btn {
    background-color: #f2f2f7;
    color: #8e8e93;
    border: none;
    padding: 10px 18px;
    border-radius: 20px;
    font-size: 16px;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    position: relative;
    min-height: 44px;
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 320px;
  }

  .quick-find-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    padding: 10px 16px;
    border: none;
    border-radius: 8px;
    background: #007aff;
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
  }

  .quick-find-btn:hover {
    background: #0056b3;
  }

  .quick-find-dialog {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px;
    font-size: 16px;
  }

  .quick-find-digits {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
  }

  .quick-find-digit {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #f9fafb;
    font-size: 24px;
    font-weight: 700;
  }

  .quick-find-keypad {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .quick-find-keypad button {
    min-height: 48px;
    border: none;
    border-radius: 8px;
    background: #f2f2f7;
    color: #1f2937;
    font-size: 22px;
    font-weight: 600;
    cursor: pointer;
  }

  .quick-find-keypad button:active {
    background: #dbeafe;
  }

  .quick-find-keypad .quick-find-key-action {
    font-size: 16px;
  }

  .quick-find-dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  .quick-find-dialog-actions button {
    min-height: 40px;
    padding: 6px 14px;
    border: 0;
    border-radius: 8px;
    background: #f2f2f7;
    color: #1f2937;
    font-size: 15px;
    cursor: pointer;
  }

  .filter-btn.active {
    background: #007aff;
    color: white;
  }

  .filter-btn-selection {
    max-width: 180px;
    overflow: hidden;
    color: #3a3a3c;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .filter-btn.active .filter-btn-selection {
    color: white;
  }

  .filter-btn .fa-chevron-down {
    transition: transform 0.2s ease;
  }

  .filter-btn .fa-chevron-down.rotated {
    transform: rotate(180deg);
  }

  .refresh-btn {
    background: #007aff;
    color: white;
    border: none;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .refresh-btn:hover {
    background: #0056b3;
  }

  .refresh-btn:active {
    transform: scale(0.98);
    opacity: 0.9;
  }

  /* 筛选下拉菜单 */
  .filter-dropdown {
    position: relative;
    display: inline-block;
  }

  .filter-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border: 1px solid #e5e5ea;
    min-width: 240px;
    z-index: 1000;
    padding: 8px 0;
    margin-top: 8px;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.2s ease;
  }

  .filter-menu.show {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .filter-menu-section {
    padding: 12px 16px 8px;
    border-bottom: 1px solid #e5e5ea;
  }

  .filter-menu-section:last-child {
    border-bottom: none;
  }

  .filter-menu-title {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;
    font-weight: 600;
    color: #8e8e93;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .filter-menu-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 16px;
    background: none;
    border: none;
    text-align: left;
    font-size: 16px;
    color: #000000;
    cursor: pointer;
    border-radius: 8px;
    margin: 2px 0;
    transition: all 0.2s;
  }

  .filter-menu-item:hover {
    background-color: rgba(0, 122, 255, 0.1);
    color: #007aff;
  }

  .filter-menu-item.active {
    background-color: #007aff;
    color: white;
  }

  .filter-menu-item i {
    width: 16px;
    margin-right: 8px;
  }

  .filter-menu-item .filter-check {
    width: auto;
    margin-right: 0;
    font-size: 18px;
  }

  .filter-menu-footer {
    padding: 8px 16px 12px;
  }

  .filter-done-btn {
    width: 100%;
    min-height: 40px;
    border: none;
    border-radius: 8px;
    background-color: #007aff;
    color: white;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
  }

  .filter-done-btn:active {
    transform: scale(0.98);
    opacity: 0.9;
  }

  /* 视图显示控制 */
  .order-view {
    display: block;
  }

  /* 标题区域布局 */
  .title-section {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .summary-info {
    display: flex;
    align-items: center;
  }

  .summary-badge {
    color: white;
    padding: 12px 14px;
    border-radius: 25px;
    font-size: 40px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(255, 59, 48, 0.3);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }

  .summary-badge.completed {
    background: linear-gradient(135deg, #34c759, #30d158);
    box-shadow: 0 2px 8px rgba(52, 199, 89, 0.3);
  }

  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }

  .summary-badge i {
    margin-right: 6px;
  }

  .summary-badge span {
    transition: all 0.3s ease;
  }

  .summary-badge span.updated {
    transform: scale(1.1);
    animation: pulse 0.6s ease;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }

  .no-order-tip {
    text-align: center;
    width: 100%;
    padding: 40px 0;
    color: #888;
    background: transparent;
    box-shadow: none;
    grid-column: 1 / -1;
  }

  .order-header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
  }

  /* 新增：订单头部详情布局 */
  .order-header-details {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 6px;
  }

  .order-info-left {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  .order-id {
    display: flex;
    align-items: baseline;
    font-size: 12px;
    font-weight: 600;
    color: #000000;
    margin-bottom: 2px;
    white-space: nowrap;
  }

  .order-id-prefix {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .order-id-suffix {
    flex-shrink: 0;
    font-size: 26px;
    font-weight: 600;
    line-height: 1.25;
  }

  .order-table {
    font-size: 26px;
    font-weight: 600;
    color: #000000;
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .order-table-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .seat-number-btn {
    display: inline-flex;
    align-items: center;
    min-height: 30px;
    padding: 4px 9px;
    border: 1px solid #60a5fa;
    border-radius: 7px;
    background: #eff6ff;
    color: #2563eb;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
  }

  .seat-number-btn:hover {
    border-color: #2563eb;
    background: #dbeafe;
  }

  .seat-number-modal {
    width: min(92vw, 720px);
    max-height: min(82vh, 680px);
  }

  .seat-table-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: 2px 6px 8px 2px;
    touch-action: pan-y;
  }

  .seat-table-option {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    min-height: 48px;
    padding: 7px 8px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #ffffff;
    color: #374151;
    font-size: 14px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  .seat-table-option:not(:disabled) {
    cursor: pointer;
  }

  .seat-table-option:not(:disabled):hover,
  .seat-table-option.selected {
    border-color: #2563eb;
    background: #eff6ff;
    color: #2563eb;
  }

  .seat-table-option:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  @media (min-width: 640px) {
    .seat-number-modal {
      padding: 24px;
    }

    .seat-table-grid {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 10px;
    }

    .seat-table-option {
      min-height: 56px;
      font-size: 16px;
    }
  }

  @media (min-width: 900px) {
    .seat-table-grid {
      grid-template-columns: repeat(5, minmax(0, 1fr));
    }
  }

  /* 新增：确认支付按钮样式 */
  .order-payment-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  .confirm-payment-btn {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: white;
    border: none;
    min-height: 38px;
    padding: 6px 8px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  }

  .confirm-payment-btn:hover {
    background: linear-gradient(135deg, #16a34a, #15803d);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
  }

  .confirm-payment-btn:active {
    transform: scale(0.98);
  }

  /* 新增：刷新时间显示样式 */
  .refresh-time-info {
    display: flex;
    align-items: center;
    font-size: 18px;
    color: #ff3b30;
    margin-left: 16px;
  }

  .refresh-time-info i {
    margin-right: 8px;
    color: #ff3b30;
  }

  .exclude-paid-toggle,
  .order-filter-toggle {
    display: flex;
    align-items: center;
    margin-left: 16px;
    cursor: pointer;
  }

  .exclude-paid-label,
  .order-filter-label {
    margin-left: 8px;
    font-size: 16px;
    color: #ffffff;
  }

  .reservation-table-list {
    flex: 1 1 auto;
    min-height: 0;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-y;
  }

  .reservation-table-modal-mask {
    overflow: hidden;
  }

  .reservation-table-modal {
    box-sizing: border-box;
    height: calc(100vh - 2rem);
    height: calc(100dvh - 2rem);
    max-height: 800px;
  }

  /* 深色背景下强化开关视觉效果 */
  .exclude-paid-toggle .ant-switch,
  .order-filter-toggle .ant-switch {
    background-color: rgba(255, 255, 255, 0.25);
    border: 1px solid #ffffff;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.6);
  }

  .exclude-paid-toggle .ant-switch-checked,
  .order-filter-toggle .ant-switch-checked {
    background-color: #52c41a;
    border-color: #52c41a;
    box-shadow: 0 0 6px rgba(82, 196, 26, 0.7);
  }

  /* 新增：导航菜单样式 */
  .nav-dropdown {
    position: relative;
    display: inline-block;
  }

  .nav-menu-btn {
    background: #007aff;
    color: white;
    border: none;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .nav-menu-btn:hover {
    background: #0056b3;
  }

  .nav-menu-btn:active {
    transform: scale(0.98);
    opacity: 0.9;
  }

  .nav-menu {
    position: absolute;
    top: 100%;
    left: 0;
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border: 1px solid #e5e5ea;
    min-width: 200px;
    z-index: 1000;
    padding: 8px 0;
    margin-top: 8px;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.2s ease;
  }

  .nav-menu.show {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .nav-menu-item {
    display: block;
    width: 100%;
    padding: 12px 16px;
    background: none;
    border: none;
    text-align: left;
    font-size: 16px;
    color: #000000;
    cursor: pointer;
    border-radius: 8px;
    margin: 2px 8px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .nav-menu-item:hover {
    background-color: rgba(0, 122, 255, 0.1);
    color: #007aff;
  }

  .nav-menu-item:active {
    transform: scale(0.98);
  }

  .nav-menu-item i {
    width: 16px;
    font-size: 14px;
  }

  /* html5-qrcode 全屏样式 - 强制全屏显示，消除黑边 */
  .qr-reader-container {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    position: relative !important;
    overflow: hidden !important;
  }

  /* html5-qrcode 的所有内部容器都强制全屏 */
  .qr-reader-container > *,
  .qr-reader-container #html5qr-container,
  .qr-reader-container #html5qr-container > * {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    position: relative !important;
  }

  /* 视频元素 - 强制覆盖整个容器，消除黑边 */
  .qr-reader-container video,
  .qr-reader-container #html5qr-container video {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    object-fit: cover !important; /* 覆盖整个容器，裁剪多余部分 */
    object-position: center !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* 画布元素 - 用于二维码识别 */
  .qr-reader-container canvas,
  .qr-reader-container #html5qr-container canvas {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    object-fit: cover !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
  }

  /* html5-qrcode 的内部容器 */
  .qr-reader-container #html5qr-container {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    position: relative !important;
    overflow: hidden !important;
  }

  /* 隐藏扫描类型切换按钮 */
  .qr-reader-container #html5-qrcode-anchor-scan-type-change {
    display: none !important;
  }

  /* 隐藏扫描框遮罩区域 */
  .qr-reader-container #qr-shaded-region {
    display: none !important;
  }

  /* 隐藏扫描框边框 */
  .qr-reader-container #qr-shaded-region,
  .qr-reader-container #qr-shaded-region > * {
    display: none !important;
  }

  /* 动态扫描线 - 类似微信扫码效果 */
  .scan-line {
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0, 255, 0, 0.8) 50%,
      transparent 100%
    );
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.6), 0 0 20px rgba(0, 255, 0, 0.4);
    animation: scanMove 2s linear infinite;
  }

  @keyframes scanMove {
    0% {
      top: 0;
      opacity: 1;
    }
    50% {
      opacity: 1;
    }
    100% {
      top: 100%;
      opacity: 0.3;
    }
  }
</style>
